import React, { Component } from 'react'
import FullPageWrapper from '../../wrappers/FullPageWrapper'
import { createRefPath } from 'global/firestoreData/appData/appRefPaths'
import { buildFcmPayload } from 'global/functions/fbNotifications'
import { _fcmTopicKeys } from 'global/cnr/contexts/useHelpers/useFcm'

class ErrorBoundary extends Component {

  state = {
    hasError: false,
    errorInfo: null,
  }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(error, errorInfo) {

    try {

      const { parentContext } = this.props

      const { states, fns, fn_cls } = parentContext ?? {}
      const { paps_state } = states ?? {}
      const { database_fns } = fns ?? {}
      const { pathViews } = paps_state ?? {}

      // --------------------------------------------------
      // BASIC ERROR INFO
      // --------------------------------------------------

      const message =
        error?.message ||
        'Unknown error'

      const stack = error?.stack || ''

      const componentStack = errorInfo?.componentStack || ''

      // --------------------------------------------------
      // PARSE COMPONENT STACK
      // --------------------------------------------------

      const parsedComponentStack = componentStack
        .split('\n')
        .map(line => line.trim())
        .filter(Boolean)
        .map(line => {

          // Remove "at "
          let cleaned = line.replace(/^at\s+/i, '')

          // Remove file refs
          cleaned = cleaned.replace(/\s+\(.+\)/, '')

          return cleaned

        })

      // --------------------------------------------------
      // PARSE JS STACK
      // --------------------------------------------------

      const stackLines = stack
        .split('\n')
        .map(line => line.trim())
        .filter(Boolean)

      const relevantStackLine =
        stackLines.find(line =>
          !line.includes('node_modules') &&
          !line.includes('react-dom') &&
          !line.includes('scheduler')
        ) ||
        stackLines[0] ||
        'No stack line found'

      // --------------------------------------------------
      // BUILD READABLE ERROR SUMMARY
      // --------------------------------------------------

      const readableComponentPath =
        parsedComponentStack
          .slice(0, 6)
          .join(' > ')

      const readableSummary = [
        `Message: ${message}`,
        `Page: ${window.location.pathname}`,
        `Time: ${new Date().toLocaleString()}`,
        `Component Path: ${readableComponentPath}`,
        `JS Stack: ${relevantStackLine}`,
      ]
        .filter(Boolean)
        .join('\n')

      // --------------------------------------------------
      // OPTIONAL BREADCRUMB SUPPORT
      // --------------------------------------------------

      const lastAction =
        window.__lastAction ||
        null

      // --------------------------------------------------
      // LOG TO CONSOLE
      // --------------------------------------------------

      console.group('🔥 ErrorBoundary caught an error')
      console.error('Readable Summary:')
      console.error(readableSummary)
      console.error('Raw Error:', error)
      console.error('Raw Component Stack:')
      console.error(componentStack)
      console.groupEnd()

      // --------------------------------------------------
      // SAVE TO FIREBASE
      // --------------------------------------------------

      const refPath = createRefPath(['_errorBoundary'])

      const dataToUpdate = {
        // readable
        readableSummary,
        message,
        relevantStackLine,
        readableComponentPath,

        // routing
        currentPath: window.location.pathname,
        currentUrl: window.location.href,

        // app state
        pathViews,

        // optional breadcrumbs
        lastAction,

        // raw data
        parsedComponentStack,
        rawComponentStack: componentStack,
        rawStack: stack,

        // timestamps
        timestamp: new Date().toISOString(),
      }

      database_fns.add_doc(refPath, dataToUpdate)

      // --------------------------------------------------
      // SEND FCM
      // --------------------------------------------------

      if (process.env.NODE_ENV !== 'development') {
        const body = [
          message,
          window.location.pathname,
          readableComponentPath
        ]
          .filter(Boolean)
          .join('\n')

        const payload = buildFcmPayload(
          null,
          _fcmTopicKeys.error,
          'App Error',
          body
        )

        fn_cls.sendFcmPayload(payload)

      }

      // --------------------------------------------------
      // UPDATE UI
      // --------------------------------------------------

      this.setState({
        errorInfo: {
          message,
          readableSummary,
          parsedComponentStack,
        }
      })
    } catch (boundaryError) {
      console.error(
        'ErrorBoundary itself failed:',
        boundaryError
      )
    }
  }

  renderStackTrace() {

    const {
      errorInfo
    } = this.state

    const stack =
      errorInfo?.parsedComponentStack ||
      []

    if (!stack.length) return null

    return (
      <ul>
        {stack.map((line, index) => (
          <li
            key={index}
            style={{
              wordBreak: 'break-word',
              marginBottom: '.35rem'
            }}
          >
            {line}
          </li>
        ))}
      </ul>
    )

  }

  render() {

    const {
      hasError,
      errorInfo
    } = this.state

    const {
      children,
      handleCancel
    } = this.props

    if (hasError) {

      return (
        <FullPageWrapper
          topperCaption='Oops, something went wrong'
          topperCaption2={errorInfo?.message || 'Application Error'}
          handleCancel={handleCancel}
          content={
            <div
              className='stack-error'
              style={{
                overflowY: 'auto',
                height: '100%',
                padding: '1rem',
              }}
            >

              <h3>Error Summary</h3>

              <pre
                style={{
                  whiteSpace: 'pre-wrap',
                  fontSize: '.9rem',
                }}
              >
                {errorInfo?.readableSummary}
              </pre>

              <h4>Component Stack</h4>

              {this.renderStackTrace()}

            </div>

          }

        />
      )

    }

    return children

  }

}

export default ErrorBoundary
